// src/components/StyleTryOn.jsx

import React, {
  Suspense, useRef, useState, useEffect, useMemo, useCallback,
} from 'react';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Html } from '@react-three/drei'; // ← Html added
import { OBJLoader }                     from 'three/examples/jsm/loaders/OBJLoader';
import * as THREE                        from 'three';
import { useNavigate }                   from 'react-router-dom';
import { getFunctions, httpsCallable }   from 'firebase/functions';
import {
  doc, updateDoc, addDoc, collection,
  serverTimestamp, increment, getDoc, getDocs, query, orderBy, limit,
} from 'firebase/firestore';
import { db, auth }        from '../firebase';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionPage    from './SubscriptionPage';
import toast               from 'react-hot-toast';

const GOLD          = '#B8860B';
const DARK          = '#010810';
const FALLBACK_HEAD = '/models/default-head.glb';
const PRIM_SCALE    = 1.45;
const PRIM_Y        = -0.85;

// ── CAMERA ───────────────────────────────────────────────────────────────────
// z=5.0, FOV=50°: visible_half_height = 5.0×tan(25°) = 2.33 > PRIM_SCALE 1.45 ✓
const CAM_POS    = [0, -0.1, 5.0];
const CAM_TARGET = [0, PRIM_Y, 0];   // always aim at head centre
const CAM_FOV    = 50;

// Wider snapshot camera — guarantees chin + crown + ears in FLUX input
const SNAP_POS = [0, -0.1, 5.4];
const SNAP_FOV = 60;

// ── MASK CONFIG ───────────────────────────────────────────────────────────────
const DEFAULT_MASK_CONFIG = {
  headCY:      0.49,
  headRX:      0.36,
  headRY:      0.40,
  faceCY:      0.54,
  faceRX:      0.145,
  faceRY:      0.155,
  cropFrac:    0.90,   // was 0.82 — chin now included
  cropUpShift: 0.01,   // was 0.04 — minimal upshift
};

const MASK_STYLE_OVERRIDES = {
  'Afro/Natural':    { headRY: 0.46, headCY: 0.47 },
  'TWA':             { headRY: 0.43, headCY: 0.48 },
  'Locs/Dreads':     { headRY: 0.44 },
  'Braids/Cornrows': { headRY: 0.44, faceCY: 0.56 },
};
const MASK_SHAPE_OVERRIDES = {
  'Round':       { headRX: 0.38, headRY: 0.38 },
  'Long/Oblong': { headRX: 0.30, headRY: 0.46, faceCY: 0.56, faceRY: 0.17 },
  'Square':      { headRX: 0.36, headRY: 0.39 },
};

const PARAM_OPTIONS = {
  gender:    ['Male', 'Female', 'Non-binary'],
  headShape: ['Round', 'Oval', 'Square', 'Diamond', 'Heart', 'Long/Oblong'],
  hairTexture: [
    'Type 1 — Straight', 'Type 2 — Wavy', 'Type 3 — Curly',
    'Type 4A — Coily', 'Type 4B — Kinky', 'Type 4C — Tight Coils (African)',
    'Arab / Mid-Eastern', 'South Asian', 'East Asian / Caucasian',
  ],
  cutType: [
    'Fade', 'Taper', 'Buzz/Caesar', 'Afro/Natural', 'TWA', 'Locs/Dreads',
    'Braids/Cornrows', '360 Waves', 'Box/Flat Top', 'Bob', 'Pixie',
    'Long Natural', 'Mohawk', 'Undercut', 'Slick Back',
  ],
  fadeLevel:  ['Low fade','Mid fade','High fade','Skin/Bald fade','Burst fade','Taper (no fade)'],
  topLength:  ['Very short (<0.5in)','Short (0.5–1in)','Medium (1–3in)','Long (3–6in)','Very long (6in+)'],
  hairColor:  ['Jet Black','Dark Brown','Medium Brown','Light Brown','Blonde','Red / Auburn','Grey / White','Dyed / Custom'],
  specialFeatures: [
    'Sharp lineup / edge-up','Natural hairline','Design shaved in','360 Waves pattern',
    'High top / flat top','Sideburn fade','Part line (hard)','Center part',
    'Beard integration','Highlighted / ombré tips',
  ],
};

// ── OBJECT URL TRACKER ────────────────────────────────────────────────────────
function useObjectURLs() {
  const refs    = useRef([]);
  const track   = useCallback((u) => { refs.current.push(u); return u; }, []);
  const release = useCallback((u) => {
    if (!u) return;
    URL.revokeObjectURL(u);
    refs.current = refs.current.filter(x => x !== u);
  }, []);
  useEffect(() => () => { refs.current.forEach(u => URL.revokeObjectURL(u)); refs.current = []; }, []);
  return { track, release };
}

// ── STYLE PARAMS ──────────────────────────────────────────────────────────────
const EMPTY_PARAMS = {
  gender:'', headShape:'', hairTexture:'', cutType:'', fadeLevel:'',
  topLength:'', hairColor:'', specialFeatures:[], userPrompt:'',
};
function useStyleParams() {
  const [params, setParams] = useState(EMPTY_PARAMS);
  const setParam      = useCallback((k,v) => setParams(p => ({...p,[k]:v})), []);
  const toggleFeature = useCallback((f) => setParams(p => ({
    ...p,
    specialFeatures: p.specialFeatures.includes(f)
      ? p.specialFeatures.filter(x => x!==f)
      : [...p.specialFeatures, f],
  })), []);
  const reset = useCallback(() => setParams(EMPTY_PARAMS), []);
  const hasAnyParam = useMemo(() =>
    Object.entries(params).some(([k,v]) =>
      k !== 'userPrompt' ? (Array.isArray(v) ? v.length>0 : v!=='') : v.trim()!==''
    ), [params]);
  const maskConfig = useMemo(() => {
    let cfg = { ...DEFAULT_MASK_CONFIG };
    if (MASK_SHAPE_OVERRIDES[params.headShape]) cfg = {...cfg, ...MASK_SHAPE_OVERRIDES[params.headShape]};
    if (MASK_STYLE_OVERRIDES[params.cutType])   cfg = {...cfg, ...MASK_STYLE_OVERRIDES[params.cutType]};
    return cfg;
  }, [params.headShape, params.cutType]);
  return { params, setParam, toggleFeature, reset, hasAnyParam, maskConfig };
}

// ── RETRY HELPER ──────────────────────────────────────────────────────────────
async function withRetry(fn, maxAttempts=3, onRetry=null) {
  let lastErr;
  for (let i=1; i<=maxAttempts; i++) {
    try { return await fn(); }
    catch (err) {
      lastErr = err;
      if (i < maxAttempts) {
        if (onRetry) onRetry(i, maxAttempts);
        await new Promise(r => setTimeout(r, 2500*i));
      }
    }
  }
  throw lastErr;
}

// ─────────────────────────────────────────────────────────────────────────────
//  CAMERA CONTROLLER
//  Snapshot uses SNAP_POS + SNAP_FOV (wider). Display camera unchanged.
// ─────────────────────────────────────────────────────────────────────────────
function CameraController({ snapshotRef }) {
  const { camera, gl, scene } = useThree();
  useEffect(() => {
    snapshotRef.current = () => {
      const savedPos  = camera.position.clone();
      const savedQuat = camera.quaternion.clone();
      const savedFov  = camera.fov;

      camera.position.set(...SNAP_POS);
      camera.fov = SNAP_FOV;
      camera.lookAt(...CAM_TARGET);
      camera.updateProjectionMatrix();
      camera.updateMatrixWorld(true);

      gl.render(scene, camera);
      const dataUrl = gl.domElement.toDataURL('image/png');

      camera.position.copy(savedPos);
      camera.quaternion.copy(savedQuat);
      camera.fov = savedFov;
      camera.updateProjectionMatrix();
      camera.updateMatrixWorld(true);

      return dataUrl;
    };
  }, [camera, gl, scene, snapshotRef]);
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
//  3D LOADER — uses <Html> from drei so it renders INSIDE Canvas safely
//  Html from drei portals HTML into the scene without breaking R3F reconciler.
// ─────────────────────────────────────────────────────────────────────────────
function CanvasLoader() {
  return (
    <Html center>
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
        fontFamily: 'Montserrat, sans-serif',
      }}>
        <div style={{
          width: '28px', height: '28px',
          border: '2px solid rgba(184,134,11,0.2)',
          borderTop: `2px solid ${GOLD}`,
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
        <p style={{ color: GOLD, fontSize: '0.55rem', letterSpacing: '2px', margin: 0, whiteSpace: 'nowrap' }}>
          LOADING YOUR FACE...
        </p>
      </div>
    </Html>
  );
}

// ── 3D HEAD COMPONENTS ────────────────────────────────────────────────────────
function GLBHead({ url, onLoad }) {
  const { scene: raw } = useGLTF(url || FALLBACK_HEAD);
  const scene = useMemo(() => raw?.clone(true), [raw]);
  useMemo(() => {
    if (!scene) return;
    scene.traverse(c => {
      if (c.isMesh && c.material) {
        c.castShadow = true; c.receiveShadow = true;
        const mats = Array.isArray(c.material) ? c.material : [c.material];
        mats.forEach(m => { m.side = THREE.FrontSide; });
      }
    });
  }, [scene]);
  useEffect(() => { if (scene && onLoad) onLoad(); }, [scene, onLoad]);
  if (!scene) return null;
  return <primitive object={scene} position={[0, PRIM_Y, 0]} scale={PRIM_SCALE} />;
}

function OBJHead({ url, onLoad }) {
  const obj = useLoader(OBJLoader, url);
  useMemo(() => {
    if (!obj) return;
    const mat = new THREE.MeshStandardMaterial({ color: '#c8c8c8', roughness: 0.65 });
    obj.traverse(c => { if (c.isMesh) c.material = mat; });
  }, [obj]);
  useEffect(() => { if (obj && onLoad) onLoad(); }, [obj, onLoad]);
  if (!obj) return null;
  return <primitive object={obj} position={[0, PRIM_Y, 0]} scale={PRIM_SCALE} />;
}

function HeadScene({ url, onLoad }) {
  const isOBJ = url?.toLowerCase().endsWith('.obj');
  return isOBJ
    ? <OBJHead url={url} onLoad={onLoad} />
    : <GLBHead url={url || FALLBACK_HEAD} onLoad={onLoad} />;
}

// ── MASK & CROP UTILITIES ─────────────────────────────────────────────────────
function generateHairMask(w=512, h=512, config=DEFAULT_MASK_CONFIG) {
  const { headCY, headRX, headRY, faceCY, faceRX, faceRY } = config;
  const cv  = document.createElement('canvas');
  cv.width  = w; cv.height = h;
  const ctx = cv.getContext('2d');
  const cx  = w * 0.5;

  ctx.fillStyle = '#000'; ctx.fillRect(0, 0, w, h);

  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.ellipse(cx, h*headCY, w*headRX, h*headRY, 0, 0, Math.PI*2);
  ctx.fill();

  const og = ctx.createRadialGradient(
    cx, h*headCY, Math.min(w*headRX, h*headRY)*0.78,
    cx, h*headCY, Math.max(w*headRX, h*headRY)*1.28
  );
  og.addColorStop(0, 'rgba(0,0,0,0)'); og.addColorStop(1, 'rgba(0,0,0,0.90)');
  ctx.fillStyle = og;
  ctx.beginPath(); ctx.ellipse(cx, h*headCY, w*headRX*1.28, h*headRY*1.28, 0, 0, Math.PI*2); ctx.fill();

  ctx.fillStyle = '#000';
  ctx.beginPath(); ctx.ellipse(cx, h*faceCY, w*faceRX, h*faceRY, 0, 0, Math.PI*2); ctx.fill();

  const hg = ctx.createRadialGradient(
    cx, h*faceCY, Math.min(w*faceRX, h*faceRY)*0.82,
    cx, h*faceCY, Math.max(w*faceRX, h*faceRY)*1.50
  );
  hg.addColorStop(0, 'rgba(0,0,0,0.90)'); hg.addColorStop(0.38, 'rgba(0,0,0,0.32)'); hg.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = hg;
  ctx.beginPath(); ctx.ellipse(cx, h*faceCY, w*faceRX*1.50, h*faceRY*1.50, 0, 0, Math.PI*2); ctx.fill();

  return cv.toDataURL('image/png');
}

function cropAndResize(dataUrl, size=512, frac=0.90, upShift=0.01) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement('canvas'); c.width = c.height = size;
      const ctx = c.getContext('2d');
      const sw  = img.width * frac;
      const sh  = img.height * frac;
      const sx  = (img.width  - sw) / 2;
      const sy  = (img.height - sh) / 2 - img.height * upShift;
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl); img.src = dataUrl;
  });
}

async function imageToBase64(source) {
  try {
    const blob = (source instanceof File || source instanceof Blob)
      ? source
      : await fetch(source).then(r => r.blob());
    return new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result.split(',')[1]); r.onerror = rej;
      r.readAsDataURL(blob);
    });
  } catch (err) { console.warn('[imageToBase64]', err.message); return null; }
}

// ── STYLE PARAM PANEL ─────────────────────────────────────────────────────────
function StyleParamPanel({ params, setParam, toggleFeature, reset, hasAnyParam }) {
  const [open, setOpen] = useState(false);
  const RadioGroup = ({ label, field, options }) => (
    <div style={{ marginBottom: '14px' }}>
      <p style={paramLabel}>{label}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {options.map(opt => {
          const active = params[field] === opt;
          return (
            <button key={opt} onClick={() => setParam(field, active ? '' : opt)}
              style={{ ...paramChip, background: active ? GOLD : 'rgba(255,255,255,0.05)', color: active ? '#000' : 'rgba(255,255,255,0.65)', border: `1px solid ${active ? GOLD : 'rgba(255,255,255,0.12)'}`, fontWeight: active ? '800' : '400' }}>
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
  const CheckGroup = ({ label, options }) => (
    <div style={{ marginBottom: '14px' }}>
      <p style={paramLabel}>{label}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {options.map(opt => {
          const active = params.specialFeatures.includes(opt);
          return (
            <button key={opt} onClick={() => toggleFeature(opt)}
              style={{ ...paramChip, background: active ? 'rgba(184,134,11,0.25)' : 'rgba(255,255,255,0.04)', color: active ? GOLD : 'rgba(255,255,255,0.55)', border: `1px solid ${active ? GOLD : 'rgba(255,255,255,0.10)'}` }}>
              {active ? '✓ ' : ''}{opt}
            </button>
          );
        })}
      </div>
    </div>
  );
  return (
    <div style={{ marginBottom: '18px', borderRadius: '14px', border: `1px solid ${open ? GOLD+'55' : 'rgba(255,255,255,0.08)'}`, background: 'rgba(184,134,11,0.03)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ color: GOLD, fontSize: '0.62rem', fontWeight: '900', letterSpacing: '2px' }}>⚡ STYLE GUIDE</span>
          <span style={{ fontSize: '0.52rem', opacity: 0.5, color: 'white' }}>Optional — improves accuracy</span>
          {hasAnyParam && <span style={{ background: GOLD, color: '#000', fontSize: '0.45rem', fontWeight: '900', padding: '2px 7px', borderRadius: '10px' }}>ACTIVE</span>}
        </div>
        <span style={{ color: GOLD, fontSize: '0.7rem' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px', maxHeight: '420px', overflowY: 'auto' }}>
          <RadioGroup label="GENDER"       field="gender"      options={PARAM_OPTIONS.gender} />
          <RadioGroup label="HEAD SHAPE"   field="headShape"   options={PARAM_OPTIONS.headShape} />
          <RadioGroup label="HAIR TEXTURE" field="hairTexture" options={PARAM_OPTIONS.hairTexture} />
          <RadioGroup label="CUT TYPE"     field="cutType"     options={PARAM_OPTIONS.cutType} />
          {(params.cutType === 'Fade' || params.cutType === 'Taper') &&
            <RadioGroup label="FADE LEVEL" field="fadeLevel" options={PARAM_OPTIONS.fadeLevel} />}
          <RadioGroup label="TOP LENGTH" field="topLength" options={PARAM_OPTIONS.topLength} />
          <RadioGroup label="HAIR COLOR"  field="hairColor"  options={PARAM_OPTIONS.hairColor} />
          <CheckGroup label="SPECIAL FEATURES" options={PARAM_OPTIONS.specialFeatures} />
          <div style={{ marginBottom: '12px' }}>
            <p style={paramLabel}>DESCRIBE IN YOUR OWN WORDS</p>
            <textarea value={params.userPrompt} onChange={e => setParam('userPrompt', e.target.value)}
              placeholder='"Low skin fade, sharp temple corners, short coily top..."'
              rows={3} style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(184,134,11,0.3)', borderRadius: '10px', color: 'white', fontSize: '0.65rem', padding: '10px 12px', resize: 'vertical', fontFamily: 'Montserrat, sans-serif', outline: 'none', lineHeight: 1.6 }} />
          </div>
          {hasAnyParam && <button onClick={reset} style={{ ...outlineBtn, padding: '8px 18px', fontSize: '0.58rem', marginTop: '4px' }}>CLEAR ALL</button>}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function StyleTryOn({ onClose, initialStyleUrl, initialStyleName, maskConfigOverride }) {
  const navigate    = useNavigate();
  const sub         = useSubscription();
  const user        = auth.currentUser;
  const snapshotRef = useRef(null);

  const { track: trackURL, release: releaseURL } = useObjectURLs();
  const { params, setParam, toggleFeature, reset: resetParams, hasAnyParam, maskConfig: derivedMaskConfig } = useStyleParams();
  const activeMaskConfig = maskConfigOverride || derivedMaskConfig;

  const [step,            setStep]            = useState('pick');
  const [scanUrl,         setScanUrl]         = useState(null);
  const [scanReady,       setScanReady]       = useState(false);
  const [headLoading,     setHeadLoading]     = useState(true);   // ← external loading state
  const [styleRef,        setStyleRef]        = useState(null);
  const [styleRefFile,    setStyleRefFile]    = useState(null);
  const [resultUrl,       setResultUrl]       = useState(null);
  const [hairDescription, setHairDescription] = useState('');
  const [styleName,       setStyleName]       = useState(initialStyleName || '');
  const [vaultStyles,     setVaultStyles]     = useState([]);
  const [analyzeMsg,      setAnalyzeMsg]      = useState('CONNECTING TO AI...');
  const [analyzeDetail,   setAnalyzeDetail]   = useState('');
  const [retryCount,      setRetryCount]      = useState(0);
  const [isSaving,        setIsSaving]        = useState(false);
  const [showSubModal,    setShowSubModal]    = useState(false);
  const [previewTab,      setPreviewTab]      = useState('result');

  useEffect(() => {
    if (!sub.loading && !sub.canStyleTryOn.allowed) setStep('gate');
  }, [sub.loading, sub.canStyleTryOn.allowed]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const d = snap.data();
          const url = d.baseHeadGlbUrl || d.lastScanUrl || d.initialRenderUrl || null;
          setScanUrl(url);
          setScanReady(!!url);
          if (!url) setHeadLoading(false); // no model to load
        } else {
          setHeadLoading(false);
        }
        const q  = query(collection(db, 'users', user.uid, 'customStyles'), orderBy('createdAt', 'desc'), limit(9));
        const vs = await getDocs(q);
        setVaultStyles(vs.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error('StyleTryOn load:', err); setHeadLoading(false); }
    })();
  }, [user]);

  useEffect(() => {
    if (initialStyleUrl && !sub.loading && sub.canStyleTryOn.allowed) {
      setStyleRef(initialStyleUrl); setStyleRefFile(null);
    }
  }, [initialStyleUrl, sub.loading]);

  const handleClose = useCallback(() => {
    typeof onClose === 'function' ? onClose() : navigate('/dashboard');
  }, [onClose, navigate]);

  const handleUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!sub.canStyleTryOn.allowed) { setShowSubModal(true); return; }
    if (styleRef?.startsWith('blob:')) releaseURL(styleRef);
    setStyleRef(trackURL(URL.createObjectURL(file)));
    setStyleRefFile(file); setStep('pick');
  };

  const pickVault = (style) => {
    if (!sub.canStyleTryOn.allowed) { setShowSubModal(true); return; }
    if (styleRef?.startsWith('blob:')) releaseURL(styleRef);
    setStyleRef(style.image || style.imageUrl); setStyleRefFile(null);
    if (style.name) setStyleName(style.name); setStep('pick');
  };

  const runAnalysis = async (styleRefUrl, styleFile) => {
    setStep('analyze'); setResultUrl(null); setHairDescription('');
    setRetryCount(0); setPreviewTab('result');

    const msgs = [
      [400,   'CAPTURING FULL HEAD...',       'Snapshot camera locks to chin + crown + ears'],
      [1800,  'READING REFERENCE STYLE...',    'Converting image for Claude Vision'],
      [4500,  'CLAUDE ANALYSING STYLE...',     'Mapping cut, fade, texture, edges, density'],
      [9000,  'FLUX INPAINTING FULL SCALP...',  'Painting hair across crown, sides, temples, nape'],
      [20000, 'FINALISING RESULT...',           'Uploading and optimising output'],
    ];
    const timers = msgs.map(([t, m, d]) => setTimeout(() => { setAnalyzeMsg(m); setAnalyzeDetail(d); }, t));

    try {
      if (!snapshotRef.current) {
        toast.error('3D head still loading. Wait a moment then try again.');
        setStep('pick'); timers.forEach(clearTimeout); return;
      }

      const rawDataUrl        = snapshotRef.current();
      const headRenderDataUrl = await cropAndResize(rawDataUrl, 512, activeMaskConfig.cropFrac, activeMaskConfig.cropUpShift);
      const maskDataUrl       = generateHairMask(512, 512, activeMaskConfig);
      const styleRefBase64    = await imageToBase64(styleFile || styleRefUrl);

      if (!styleRefBase64) {
        toast.error('Could not read reference photo. Please try again.');
        setStep('pick'); timers.forEach(clearTimeout); return;
      }

      const fn             = getFunctions();
      const applyHairStyle = httpsCallable(fn, 'applyHairStyle', { timeout: 170000 });

      const result = await withRetry(
        () => applyHairStyle({
          headRenderDataUrl, maskDataUrl,
          styleReferenceBase64: styleRefBase64,
          userStyleParams: params, userStylePrompt: params.userPrompt,
          userId: user?.uid,
        }),
        3,
        (attempt, max) => { setRetryCount(attempt); toast(`Retrying (${attempt}/${max-1})...`, { icon: '🔄' }); }
      );

      timers.forEach(clearTimeout);
      const { resultUrl: aiUrl, hairDescription: desc } = result.data;
      if (!aiUrl) throw new Error('Backend returned no result URL.');
      setResultUrl(aiUrl); if (desc) setHairDescription(desc); setStep('preview');

    } catch (err) {
      timers.forEach(clearTimeout);
      console.error('StyleTryOn error:', err);
      toast.error(
        err?.message?.includes('timeout')
          ? 'AI took too long. Please try again.'
          : `Style generation failed: ${err?.message || 'Unknown error'}`
      );
      setStep('pick');
    }
  };

  const handleExport = () => {
    if (!resultUrl) { toast.error('No result to export.'); return; }
    const a = document.createElement('a'); a.download = 'esdras_hairstyle_tryon.png'; a.href = resultUrl; a.click();
  };

 const handleSave = async () => {
   // Early validation
   if (!styleName?.trim()) {
     toast.error("Give this look a name first.");
     return;
   }

   if (!user || isSaving) return;

   if (!sub?.canStyleTryOn?.allowed) {
     setShowSubModal(true);
     return;
   }

   if (!resultUrl) {
     toast.error("AI result not ready.");
     return;
   }

   setIsSaving(true);

   try {
     const styleNameTrimmed = styleName.trim().toUpperCase();

     await addDoc(collection(db, "users", user.uid, "customStyles"), {
       name: styleNameTrimmed,
       image: resultUrl,
       styleReference: styleRef,
       hairDescription: hairDescription || null,
       source: "StyleTryOn",
       type: "style_tryon",
       cranialScan: scanUrl || null,
       createdAt: serverTimestamp(),
     });

     toast.success(`Style "${styleNameTrimmed}" saved successfully!`);
    
     // Optional: reset form or close modal
     // setStyleName("");
     // onSaveSuccess?.();

   } catch (error) {
     console.error("Error saving custom style:", error);
     toast.error("Failed to save style. Please try again.");
   } finally {
     setIsSaving(false);
   }
 };

  const resetToPick = () => {
    if (styleRef?.startsWith('blob:')) releaseURL(styleRef);
    setStyleRef(null); setStyleRefFile(null); setResultUrl(null);
    setHairDescription(''); setStyleName(''); setStep('pick');
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={overlay}>
      <style>{`
        @keyframes spin     { to { transform: rotate(360deg); } }
        @keyframes scanMove { 0% { top:5%; } 100% { top:90%; } }
        @keyframes fadeUp   { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
        @keyframes pulse    { 0%,100% { opacity:0.6; } 50% { opacity:1; } }
      `}</style>

      {showSubModal && (
        <SubscriptionPage paywallMode paywallReason="style_exhausted"
          onClose={() => setShowSubModal(false)} redirectAfter="/dashboard" />
      )}

      <div style={modal}>

        {/* ── HEADER ── */}
        <div style={headerBar}>
          <div>
            <h2 style={modalTitle}>STYLE TRY-ON</h2>
            <p style={modalSub}>Claude Vision · FLUX Fill Pro · Full head · All hair types</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {!sub.loading && !sub.isSuperAdmin && (
              <span style={quotaChip}>
                {sub.isSubscriptionActive
                  ? `👑 ${sub.tier?.toUpperCase()} · Unlimited`
                  : sub.canStyleTryOn.allowed ? '✓ 1 free try-on' : '🔒 Upgrade required'}
              </span>
            )}
            <button onClick={handleClose} style={xBtn}>✕</button>
          </div>
        </div>

        <div style={twoColBody}>

          {/* ── LEFT COLUMN ── */}
          <div style={leftCol}>

            {step === 'pick' && (
              <div style={{ animation: 'fadeUp 0.4s ease' }}>
                <span style={colLabel}>1. CHOOSE A STYLE REFERENCE</span>
                <label style={uploadZone}>
                  <span style={{ fontSize: '2rem' }}>📸</span>
                  <span style={{ color: GOLD, fontWeight: '700', fontSize: '0.88rem' }}>Upload a Style Photo</span>
                  <span style={{ fontSize: '0.62rem', opacity: 0.5 }}>Fades, waves, afros, braids, weaves, locs, bobs — any gender</span>
                  <input type="file" hidden accept="image/*" onChange={handleUpload} />
                </label>
                <StyleParamPanel params={params} setParam={setParam} toggleFeature={toggleFeature}
                  reset={resetParams} hasAnyParam={hasAnyParam} />
                <div style={orDivider}>OR PICK FROM YOUR VAULT</div>
                {vaultStyles.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', opacity: 0.55 }}>
                    <p style={{ fontSize: '0.75rem', marginBottom: '12px' }}>No saved styles yet.</p>
                    <button onClick={() => navigate('/gallery')} style={outlineBtn}>BROWSE GLOBAL VAULT →</button>
                  </div>
                ) : (
                  <div style={vaultGrid}>
                    {vaultStyles.map(s => (
                      <div key={s.id} style={vaultCard} onClick={() => pickVault(s)}>
                        <img src={s.image || s.imageUrl} alt={s.name} style={vaultThumb} />
                        <p style={vaultName}>{s.name}</p>
                      </div>
                    ))}
                  </div>
                )}
                {styleRef && (
                  <div style={{ marginTop: '20px' }}>
                    <button onClick={() => runAnalysis(styleRef, styleRefFile)}
                      style={{ ...goldBtn, width: '100%', padding: '18px', fontSize: '0.95rem', marginBottom: '10px' }}>
                      ✨ APPLY THIS STYLE TO MY FACE
                    </button>
                    <p style={{ fontSize: '0.58rem', color: '#888', textAlign: 'center', lineHeight: 1.5 }}>
                      Full scalp — crown, sides, temples, nape and chin hairline
                    </p>
                  </div>
                )}
                <div style={explainerBox}>
                  <p style={{ color: GOLD, fontSize: '0.6rem', fontWeight: '900', margin: '0 0 6px', letterSpacing: '1px' }}>HOW IT WORKS</p>
                  <p style={{ fontSize: '0.6rem', opacity: 0.6, margin: 0, lineHeight: 1.8 }}>
                    1. Snapshot camera pulls back — captures chin + crown + both ears.<br />
                    2. Claude Vision reads the cut, fade, nape, density from reference.<br />
                    3. Style Guide adds fade level, texture and special feature precision.<br />
                    4. FLUX paints hair across your FULL scalp — no bald patches.<br />
                    5. Switch to 🧊 3D HEAD tab to rotate your cranial solid 360°.
                  </p>
                </div>
              </div>
            )}

            {step === 'preview' && (
              <div style={{ animation: 'fadeUp 0.4s ease', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <span style={colLabel}>REFERENCE PHOTO</span>
                <div style={refImageBox}>
                  <img src={styleRef} alt="Style reference" style={refImageEl} />
                  <span style={refBadge}>REFERENCE</span>
                </div>
                {hairDescription && (
                  <div style={descBox}>
                    <p style={{ color: GOLD, fontSize: '0.58rem', fontWeight: '900', margin: '0 0 5px', letterSpacing: '1px' }}>CLAUDE DETECTED ✓</p>
                    <p style={{ fontSize: '0.7rem', opacity: 0.85, margin: 0, lineHeight: 1.7, fontStyle: 'italic' }}>"{hairDescription}"</p>
                  </div>
                )}
                {hasAnyParam && (
                  <div style={{ ...descBox, marginTop: '10px', background: 'rgba(255,255,255,0.03)' }}>
                    <p style={{ color: GOLD, fontSize: '0.58rem', fontWeight: '900', margin: '0 0 5px', letterSpacing: '1px' }}>YOUR STYLE GUIDE ✓</p>
                    <p style={{ fontSize: '0.6rem', opacity: 0.65, margin: 0, lineHeight: 1.7 }}>
                      {[params.gender, params.cutType, params.fadeLevel, params.hairColor, params.hairTexture].filter(Boolean).join(' · ')}
                      {params.userPrompt ? ` · "${params.userPrompt.substring(0, 80)}..."` : ''}
                    </p>
                  </div>
                )}
                <div style={tipBox}>
                  <p style={{ fontSize: '0.62rem', opacity: 0.75, margin: 0, lineHeight: 1.8 }}>
                    <strong style={{ color: GOLD }}>✨ AI RESULT</strong> — full scalp, chin to crown.<br />
                    <strong style={{ color: GOLD }}>🧊 3D HEAD</strong> — drag to rotate and see all sides.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div style={rightCol}>

            {step === 'preview' && (
              <div style={tabRow}>
                <button onClick={() => setPreviewTab('result')}
                  style={{ ...tabBtn, ...(previewTab === 'result' ? tabBtnActive : {}) }}>✨ AI RESULT</button>
                <button onClick={() => setPreviewTab('3d')}
                  style={{ ...tabBtn, ...(previewTab === '3d' ? tabBtnActive : {}) }}>🧊 3D HEAD</button>
              </div>
            )}
            {step !== 'preview' && <span style={colLabel}>YOUR PHOTOREALISTIC 3D HEAD</span>}

            {/* AI result panel — shown when user picks result tab */}
            {step === 'preview' && previewTab === 'result' && resultUrl && (
              <div style={aiPanel}>
                <img src={resultUrl} alt="AI hairstyle result" style={aiImg}
                  onError={() => toast.error('Could not display result.')} />
                <div style={aiBadge}>✓ FLUX Fill Pro · Full scalp</div>
                <div style={aiFooter}>Switch to 🧊 3D HEAD to rotate</div>
              </div>
            )}

            {/*
              ── SINGLE CANVAS — always mounted, visibility controlled via CSS ──
              KEY FIX: Canvas is NEVER unmounted, only hidden via visibility/display.
              This prevents GLB reloads on tab switches and keeps snapshotRef live.

              The external `headLoading` state drives the overlay loader OUTSIDE
              Canvas — NO HTML inside Canvas, eliminating the R3F namespace crash.
            */}
            <div style={{
              ...canvasWrapper,
              // Hide canvas when AI result is showing; still mounted for snapshot capability
              display: (step === 'preview' && previewTab === 'result' && resultUrl) ? 'none' : 'block',
            }}>

              {/* External loading overlay — HTML OUTSIDE Canvas, never inside */}
              {headLoading && scanUrl && (
                <div style={loadingOverlay}>
                  <div style={spinnerStyle} />
                  <p style={{ color: GOLD, fontSize: '0.55rem', letterSpacing: '2px', margin: '10px 0 0', fontFamily: 'Montserrat, sans-serif' }}>
                    LOADING YOUR FACE...
                  </p>
                </div>
              )}

              <Canvas
                key="tryon-canvas-stable"
                frameloop="always"
                gl={{ preserveDrawingBuffer: true, powerPreference: 'high-performance', antialias: true }}
                camera={{ position: CAM_POS, fov: CAM_FOV }}
                style={{ position: 'absolute', inset: 0 }}
              >
                {/* Camera controller registers snapshot function */}
                <CameraController snapshotRef={snapshotRef} />

                <color attach="background" args={['#0a0a0a']} />
                <ambientLight intensity={0.55} />
                <directionalLight position={[4, 8, 5]}   intensity={1.4} castShadow />
                <directionalLight position={[-3, 2, -4]} intensity={0.3} color="#99aaff" />
                <pointLight       position={[0, 6, 3]}   intensity={0.5} color="#ffe8cc" />
                <pointLight       position={[0, -2, 3]}  intensity={0.2} />

                {/*
                  Suspense fallback is null — NO HTML inside Canvas.
                  Loading is handled by the external overlay div above.
                  CanvasLoader uses <Html> from drei as an optional alternative
                  (safe because drei's Html uses a portal, not raw reconciler children).
                */}
                <Suspense fallback={<CanvasLoader />}>
                  <HeadScene
                    url={scanUrl}
                    onLoad={() => setHeadLoading(false)}
                  />
                </Suspense>

                <OrbitControls
                  enablePan={false}
                  target={CAM_TARGET}
                  minDistance={3.0}
                  maxDistance={9.0}
                  minPolarAngle={Math.PI / 6}
                  maxPolarAngle={Math.PI / 1.25}
                />
              </Canvas>

              {/* No-scan overlay — HTML OUTSIDE Canvas, positioned absolute within wrapper */}
              {step === 'pick' && !scanReady && !headLoading && (
                <div style={noScanOverlay}>
                  <p style={{ fontSize: '0.72rem', opacity: 0.65, marginBottom: '14px', maxWidth: '200px', textAlign: 'center' }}>
                    Complete a 3D scan first — your photorealistic head appears here.
                  </p>
                  <button onClick={() => { handleClose(); navigate('/scan'); }} style={goldBtn}>
                    START 3D SCAN
                  </button>
                </div>
              )}
            </div>

            <p style={hintText}>
              {step === 'preview' && previewTab === 'result'
                ? 'Full head captured — crown to chin, both ears visible'
                : step === 'preview'
                  ? '🖱 Drag to rotate · Scroll to zoom · See all sides'
                  : 'Snapshot locks to front view — rotate freely for display'}
            </p>
          </div>

          {/* ── STEP OVERLAYS (inside twoColBody, position:absolute) ── */}

          {step === 'gate' && (
            <div style={stepOverlay}>
              <div style={{ fontSize: '3rem', marginBottom: '18px' }}>🔒</div>
              <h3 style={{ color: GOLD, letterSpacing: '2px', margin: '0 0 10px' }}>STYLE TRY-ON LOCKED</h3>
              <p style={{ opacity: 0.65, maxWidth: '320px', textAlign: 'center', lineHeight: 1.65, marginBottom: '26px' }}>
                Your free try-on has been used. Subscribe for unlimited projections.
              </p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button onClick={() => setShowSubModal(true)} style={goldBtn}>MONTHLY — ₦3,000/mo</button>
                <button onClick={() => setShowSubModal(true)} style={outlineBtn}>YEARLY — ₦20,000/yr</button>
              </div>
              <button onClick={handleClose} style={{ ...outlineBtn, marginTop: '14px' }}>BACK TO DASHBOARD</button>
            </div>
          )}

          {step === 'analyze' && (
            <div style={stepOverlay}>
              {styleRef && (
                <div style={analyzeImgBox}>
                  <img src={styleRef} alt="Reference" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', opacity: 0.45 }} />
                  <div style={scanLine} />
                </div>
              )}
              <p style={{ color: GOLD, letterSpacing: '3px', fontSize: '0.75rem', fontWeight: '700', margin: '0 0 6px' }}>{analyzeMsg}</p>
              <p style={{ opacity: 0.4, fontSize: '0.6rem', margin: '0 0 24px' }}>{analyzeDetail}</p>
              {retryCount > 0 && <p style={{ color: '#ff9900', fontSize: '0.58rem', margin: '0 0 12px' }}>Retry {retryCount} of 3…</p>}
              <div style={progressSteps}>
                {[
                  'Full-head snapshot (chin + crown)',
                  'Convert reference photo',
                  'Claude reads style details',
                  'FLUX paints full scalp',
                  'Upload output',
                ].map((label, i) => {
                  const keywords = ['CAPTURING', 'READING', 'ANALYSING', 'FLUX', 'FINALISING'];
                  const idx      = keywords.findIndex(m => analyzeMsg.includes(m));
                  const done     = i <= idx;
                  const current  = i === idx + 1;
                  return (
                    <div key={i} style={progressStep}>
                      <div style={{
                        width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                        background: done ? GOLD : current ? 'rgba(184,134,11,0.4)' : 'rgba(255,255,255,0.1)',
                        border: done ? 'none' : `1px solid ${current ? GOLD : 'rgba(255,255,255,0.2)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.55rem', color: 'black',
                        animation: current ? 'pulse 1.2s ease infinite' : 'none',
                      }}>
                        {done && '✓'}
                      </div>
                      <span style={{ fontSize: '0.62rem', opacity: done ? 0.9 : 0.4 }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'name' && (
            <div style={stepOverlay}>
              <div style={nameCircle}>
                <img src={resultUrl || styleRef} alt="Style" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <span style={{ ...colLabel, display: 'block', textAlign: 'center', marginBottom: '14px' }}>NAME THIS LOOK</span>
              <input style={nameInput} placeholder="E.G. CLEAN LOW FADE" value={styleName}
                onChange={e => setStyleName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSave()} autoFocus />
              <button onClick={handleSave} disabled={isSaving}
                style={{ ...goldBtn, padding: '16px 44px', opacity: isSaving ? 0.7 : 1 }}>
                {isSaving ? 'VAULTING...' : '🔒 SECURE IN 3D VAULT'}
              </button>
              <button onClick={() => setStep('preview')} style={{ ...outlineBtn, marginTop: '10px' }}>← BACK TO PREVIEW</button>
            </div>
          )}

          {step === 'success' && (
            <div style={stepOverlay}>
              <div style={successRing}>✓</div>
              <h2 style={{ color: GOLD, letterSpacing: '4px', margin: '0 0 10px', fontSize: '1.5rem' }}>VAULTED</h2>
              <p style={{ opacity: 0.65, marginBottom: '28px' }}>"{styleName}" is secured in your personal lookbook.</p>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button onClick={() => navigate('/gallery')} style={goldBtn}>FIND A STYLIST</button>
                <button onClick={() => navigate('/lookbook')} style={outlineBtn}>VIEW LOOKBOOK</button>
                <button onClick={resetToPick} style={outlineBtn}>TRY ANOTHER STYLE</button>
              </div>
            </div>
          )}

        </div>

        {step === 'preview' && (
          <div style={actionBar}>
            <button onClick={resetToPick} style={outlineBtn}>← CHANGE STYLE</button>
            <button onClick={handleExport} style={outlineBtn}>📸 SAVE SNAPSHOT</button>
            <button onClick={() => setStep('name')} style={{ ...goldBtn, padding: '13px 28px' }}>
              NAME &amp; VAULT THIS LOOK →
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const overlay       = { position:'fixed', inset:0, background:'rgba(0,0,0,0.97)', zIndex:9000, display:'flex', alignItems:'stretch', justifyContent:'center', fontFamily:'Montserrat, sans-serif' };
const modal         = { width:'100%', maxWidth:'1120px', display:'flex', flexDirection:'column', background:DARK, color:'white', overflow:'hidden' };
const headerBar     = { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 28px', borderBottom:'1px solid rgba(184,134,11,0.2)', flexShrink:0 };
const modalTitle    = { color:GOLD, fontSize:'1.25rem', fontWeight:'900', letterSpacing:'3px', margin:0 };
const modalSub      = { fontSize:'0.5rem', opacity:0.35, margin:'3px 0 0' };
const xBtn          = { background:'rgba(255,255,255,0.08)', border:'none', color:'white', width:'34px', height:'34px', borderRadius:'50%', cursor:'pointer', fontSize:'0.9rem', flexShrink:0 };
const quotaChip     = { fontSize:'0.6rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'20px', padding:'5px 12px', color:GOLD, whiteSpace:'nowrap' };
const twoColBody    = { flex:1, display:'flex', overflow:'hidden', minHeight:0, position:'relative' };
const leftCol       = { flex:1, padding:'24px 22px 24px 28px', overflowY:'auto', borderRight:'1px solid rgba(255,255,255,0.06)', minWidth:0 };
const rightCol      = { width:'46%', minWidth:'340px', maxWidth:'500px', padding:'24px 28px 24px 22px', display:'flex', flexDirection:'column', flexShrink:0 };
const colLabel      = { fontSize:'0.58rem', color:GOLD, fontWeight:'900', letterSpacing:'2.5px', marginBottom:'14px', display:'block' };
const uploadZone    = { display:'flex', flexDirection:'column', alignItems:'center', gap:'7px', border:`2px dashed ${GOLD}55`, borderRadius:'16px', padding:'24px 18px', cursor:'pointer', marginBottom:'14px', background:'rgba(184,134,11,0.03)' };
const orDivider     = { textAlign:'center', fontSize:'0.56rem', color:'#2a2a2a', fontWeight:'700', letterSpacing:'2px', margin:'14px 0' };
const vaultGrid     = { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px' };
const vaultCard     = { borderRadius:'10px', overflow:'hidden', cursor:'pointer', border:'1px solid rgba(255,255,255,0.07)' };
const vaultThumb    = { width:'100%', height:'70px', objectFit:'cover', display:'block' };
const vaultName     = { fontSize:'0.5rem', fontWeight:'700', padding:'4px', textAlign:'center', color:GOLD, margin:0 };
const explainerBox  = { marginTop:'20px', padding:'14px 16px', background:'rgba(255,255,255,0.03)', borderRadius:'12px', border:'1px solid rgba(255,255,255,0.07)' };
const tipBox        = { marginTop:'14px', padding:'12px 16px', background:'rgba(184,134,11,0.06)', borderRadius:'12px', border:'1px solid rgba(184,134,11,0.2)' };
const refImageBox   = { position:'relative', borderRadius:'16px', overflow:'hidden', border:'1px solid rgba(184,134,11,0.25)', height:'200px', flexShrink:0 };
const refImageEl    = { width:'100%', height:'100%', objectFit:'cover', display:'block' };
const refBadge      = { position:'absolute', top:'10px', left:'10px', background:'rgba(0,0,0,0.7)', color:'white', fontSize:'0.55rem', fontWeight:'900', padding:'3px 9px', borderRadius:'5px' };
const descBox       = { marginTop:'12px', padding:'12px 16px', background:'rgba(184,134,11,0.08)', borderRadius:'12px', border:'1px solid rgba(184,134,11,0.25)' };
const tabRow        = { display:'flex', gap:'8px', marginBottom:'14px', flexShrink:0 };
const tabBtn        = { flex:1, padding:'8px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.04)', color:'rgba(255,255,255,0.5)', cursor:'pointer', fontSize:'0.65rem', fontWeight:'700', letterSpacing:'1px' };
const tabBtnActive  = { background:'rgba(184,134,11,0.18)', border:`1px solid ${GOLD}`, color:GOLD };
const aiPanel       = { flex:1, position:'relative', borderRadius:'20px', overflow:'hidden', border:`1px solid ${GOLD}44`, background:'#0a0a0a', display:'flex', alignItems:'center', justifyContent:'center', minHeight:'380px' };
const aiImg         = { width:'100%', height:'100%', objectFit:'contain', display:'block' };
const aiBadge       = { position:'absolute', top:'10px', right:'10px', background:GOLD, color:'black', fontSize:'0.55rem', fontWeight:'900', padding:'5px 12px', borderRadius:'6px' };
const aiFooter      = { position:'absolute', bottom:'10px', left:'50%', transform:'translateX(-50%)', background:'rgba(0,0,0,0.75)', color:'rgba(255,255,255,0.45)', fontSize:'0.5rem', padding:'4px 12px', borderRadius:'20px', whiteSpace:'nowrap' };
const canvasWrapper = { position:'relative', flex:1, minHeight:'460px', borderRadius:'20px', overflow:'hidden', border:'1px solid rgba(184,134,11,0.2)' };
const loadingOverlay= { position:'absolute', inset:0, zIndex:5, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(1,8,16,0.85)', borderRadius:'20px' };
const spinnerStyle  = { width:'28px', height:'28px', border:'2px solid rgba(184,134,11,0.2)', borderTop:`2px solid ${GOLD}`, borderRadius:'50%', animation:'spin 0.8s linear infinite' };
const noScanOverlay = { position:'absolute', inset:0, zIndex:5, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(1,8,16,0.9)', borderRadius:'20px', padding:'20px', textAlign:'center' };
const hintText      = { fontSize:'0.52rem', opacity:0.3, textAlign:'center', marginTop:'8px', flexShrink:0 };
const stepOverlay   = { position:'absolute', inset:0, background:DARK, zIndex:10, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'28px', textAlign:'center', animation:'fadeUp 0.25s ease' };
const analyzeImgBox = { width:'160px', height:'160px', margin:'0 auto 20px', position:'relative', overflow:'hidden', borderRadius:'16px', border:`2px solid ${GOLD}` };
const scanLine      = { position:'absolute', left:0, right:0, height:'3px', background:GOLD, boxShadow:`0 0 12px ${GOLD}`, animation:'scanMove 1.6s ease-in-out infinite alternate' };
const progressSteps = { display:'flex', flexDirection:'column', gap:'10px', alignItems:'flex-start', maxWidth:'280px', marginTop:'8px' };
const progressStep  = { display:'flex', alignItems:'center', gap:'12px' };
const nameCircle    = { width:'150px', height:'150px', borderRadius:'50%', overflow:'hidden', border:`3px solid ${GOLD}`, margin:'0 auto 20px' };
const nameInput     = { background:'none', border:'none', borderBottom:`2px solid ${GOLD}`, color:'white', fontSize:'1.15rem', textAlign:'center', outline:'none', marginBottom:'24px', width:'100%', maxWidth:'360px', textTransform:'uppercase', padding:'8px 0', fontFamily:'Montserrat, sans-serif' };
const successRing   = { fontSize:'2.8rem', color:GOLD, border:`3px solid ${GOLD}`, borderRadius:'50%', width:'68px', height:'68px', display:'grid', placeItems:'center', margin:'0 auto 16px' };
const actionBar     = { display:'flex', gap:'10px', padding:'14px 28px', borderTop:'1px solid rgba(255,255,255,0.06)', justifyContent:'flex-end', flexWrap:'wrap', flexShrink:0 };
const goldBtn       = { background:GOLD, color:'black', border:'none', padding:'12px 24px', borderRadius:'50px', fontWeight:'900', cursor:'pointer', fontSize:'0.75rem', whiteSpace:'nowrap', fontFamily:'Montserrat, sans-serif' };
const outlineBtn    = { background:'rgba(255,255,255,0.06)', color:'white', border:'1px solid rgba(255,255,255,0.15)', padding:'12px 20px', borderRadius:'50px', cursor:'pointer', fontSize:'0.73rem', fontWeight:'600', whiteSpace:'nowrap', fontFamily:'Montserrat, sans-serif' };
const paramLabel    = { fontSize:'0.52rem', color:GOLD, fontWeight:'900', letterSpacing:'1.5px', margin:'0 0 6px' };
const paramChip     = { padding:'4px 10px', borderRadius:'20px', cursor:'pointer', fontSize:'0.55rem', fontFamily:'Montserrat, sans-serif', letterSpacing:'0.5px', transition:'all 0.15s' };
